/**
 * Platform Health — PRISM workbench (A7Y-2330).
 *
 * One page for the quality of customer-facing ASO output: the PRISM verdict,
 * the quality registry and its exact denominators, the Audit Assertions view
 * with its legacy parity, the calibration lanes, the unified findings, the
 * decisions / actions / receipts / recovery, self-health and compatibility,
 * and a review packaged for Yevhen in ASO language.
 *
 * Every status, verdict and parity state is computed by the API
 * (`buildPrismWorkbench`); this page renders and never re-derives. Two
 * rendering rules follow from the ticket: a verdict other than `expert_grade`
 * is never shown green, and a review item without a plain-language label
 * shows its id beside an explicit marker rather than as a title.
 */
import { PageHeader } from '@/components/PageHeader';
import { PrismPreSendPanel } from '@/pages/platform-health/PrismPreSendPanel';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type {
  PlatformHealthPrismResponse,
  PrismWorkbenchCheck,
  PrismWorkbenchCheckStatus,
  PrismWorkbenchFinding,
  PrismWorkbenchLaneRun,
  PrismWorkbenchVerdict,
} from '@/pages/platform-health/types';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  HelpCircle,
  RefreshCw,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const ENVIRONMENTS = ['default', 'production', 'staging', 'ci', 'development'];

function display(value: string) {
  return value.replace(/_/g, ' ');
}

function timestamp(value: string | null) {
  return value ? new Date(value).toLocaleString() : 'not observed';
}

function pct(value: number | null) {
  return value === null ? '—' : `${Math.round(value * 100)}%`;
}

type Tone = 'neutral' | 'red' | 'amber' | 'green' | 'blue';

function statusTone(status: PrismWorkbenchCheckStatus): Tone {
  if (status === 'clean') return 'green';
  if (status === 'not_applicable') return 'neutral';
  if (status === 'failing') return 'amber';
  return 'red'; // never_seen, missing, errored — never green
}

function verdictTone(status: PrismWorkbenchVerdict): Tone {
  if (status === 'expert_grade') return 'green';
  if (status === 'unknown') return 'amber';
  return 'red';
}

function outcomeTone(outcome: PrismWorkbenchLaneRun['outcome']): Tone {
  if (outcome === 'pass') return 'green';
  if (outcome === 'fail' || outcome === 'warn') return 'amber';
  if (outcome === 'skipped') return 'neutral';
  return 'red';
}

function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: Tone }) {
  const colors: Record<Tone, string> = {
    neutral: 'bg-gray-100 text-gray-700 dark:bg-zinc-700 dark:text-gray-200',
    red: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200',
    amber: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
    green: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
    blue: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200',
  };
  return (
    <span
      className={cn(
        'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
        colors[tone]
      )}
    >
      {children}
    </span>
  );
}

function Metric({
  label,
  value,
  warning = false,
}: {
  label: string;
  value: number | string;
  warning?: boolean;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-800">
      <dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt>
      <dd
        className={cn(
          'mt-1 text-2xl font-semibold tabular-nums text-gray-950 dark:text-white',
          warning && Number(value) > 0 && 'text-red-700 dark:text-red-300'
        )}
      >
        {value}
      </dd>
    </div>
  );
}

function Section({
  title,
  subtitle,
  testId,
  children,
}: {
  title: string;
  subtitle?: string;
  testId: string;
  children: ReactNode;
}) {
  return (
    <section
      data-testid={testId}
      className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-800"
    >
      <header className="border-b border-gray-200 px-4 py-2.5 dark:border-zinc-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
        {subtitle ? (
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
        ) : null}
      </header>
      {children}
    </section>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return (
    <p className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">{children}</p>
  );
}

function VerdictBanner({ workbench }: { workbench: PlatformHealthPrismResponse['workbench'] }) {
  const { verdict } = workbench;
  const tone = verdictTone(verdict.status);
  const Icon =
    verdict.status === 'expert_grade'
      ? CheckCircle2
      : verdict.status === 'unknown'
        ? HelpCircle
        : XCircle;
  return (
    <div
      data-testid="prism-verdict"
      data-verdict={verdict.status}
      className={cn(
        'rounded-lg border p-4 text-sm',
        tone === 'green' &&
          'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100',
        tone === 'amber' &&
          'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100',
        tone === 'red' &&
          'border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100'
      )}
    >
      <div className="flex items-center gap-2 font-semibold">
        <Icon className="h-4 w-4" aria-hidden="true" />
        PRISM verdict: {display(verdict.status)}
      </div>
      <p className="mt-1 text-xs">{verdict.explanation}</p>
      {verdict.reasons.length > 0 ? (
        <ul className="mt-2 list-disc pl-5 text-xs">
          {verdict.reasons.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function CheckRow({ check }: { check: PrismWorkbenchCheck }) {
  const d = check.denominators;
  return (
    <li
      data-testid="prism-check-row"
      data-check-id={check.checkId}
      data-status={check.status}
      className="border-b border-gray-200 px-4 py-3 last:border-b-0 dark:border-zinc-700"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-gray-950 dark:text-white">
          {check.displayName}
        </span>
        <Badge tone={statusTone(check.status)}>{display(check.status)}</Badge>
        <Badge>{display(check.laneKind)}</Badge>
        <Badge tone={check.checkType === 'judged' ? 'blue' : 'neutral'}>
          {display(check.checkType)}
        </Badge>
        {check.activeFindings > 0 ? <Badge tone="amber">{check.activeFindings} open</Badge> : null}
      </div>
      <p className="mt-1 font-mono text-[11px] text-gray-500">{check.checkId}</p>
      <p className="mt-1 text-xs text-gray-700 dark:text-gray-200">{check.explanation}</p>
      <p className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-gray-500 dark:text-gray-400">
        <span>audits {d.audits}</span>
        <span>rows {d.rows}</span>
        <span>passed {d.passed}</span>
        <span>failed {d.failed}</span>
        <span>missing {d.missing}</span>
        <span>errored {d.errored}</span>
        <span>n/a {d.not_applicable}</span>
        <span>waived {d.waived}</span>
        <span>recovered {d.recovered}</span>
        <span>last {timestamp(check.lastObservedAt)}</span>
      </p>
    </li>
  );
}

function LaneRow({ run }: { run: PrismWorkbenchLaneRun }) {
  const a = run.accounting;
  const ravenLink = run.releaseId
    ? run.environment === 'staging'
      ? `/admin/platform-health/staging-deploys/${encodeURIComponent(run.releaseId)}`
      : '/admin/platform-health/prod-deploys'
    : null;
  return (
    <details
      data-testid="prism-lane-row"
      data-lane={run.lane}
      className="group border-b border-gray-200 last:border-b-0 dark:border-zinc-700"
    >
      <summary className="flex cursor-pointer list-none flex-wrap items-center gap-2 px-4 py-3">
        <ChevronRight
          className="h-4 w-4 text-gray-400 transition-transform group-open:rotate-90"
          aria-hidden="true"
        />
        <span className="text-sm font-semibold text-gray-950 dark:text-white">
          {display(run.lane)}
        </span>
        <Badge tone={run.applicability === 'not_applicable' ? 'neutral' : outcomeTone(run.outcome)}>
          {run.applicability === 'not_applicable'
            ? 'not applicable'
            : (run.outcome ?? 'no attestation')}
        </Badge>
        <Badge>{run.environment}</Badge>
        {run.action ? (
          <Badge tone={run.action.wouldBlock ? 'red' : 'neutral'}>
            action {run.action.modeEffective ?? 'unknown'}
            {run.action.wouldBlock ? ' · would block' : ''}
          </Badge>
        ) : null}
        <span className="font-mono text-[11px] text-gray-500">{run.runId}</span>
        <span className="text-[11px] text-gray-500">{timestamp(run.capturedAt)}</span>
      </summary>
      <div className="grid gap-3 border-t border-gray-100 bg-gray-50/70 px-4 py-3 text-xs dark:border-zinc-700 dark:bg-zinc-900/40 lg:grid-cols-3">
        <div>
          <p className="font-semibold text-gray-700 dark:text-gray-200">Accounting</p>
          <p className="mt-1 text-gray-600 dark:text-gray-300">
            declared {a.declared} · observed {a.observed} · clean {a.clean} · violated {a.violated}{' '}
            · errored {a.errored} · missing {a.missing} · never seen {a.neverSeen} · n/a{' '}
            {a.notApplicable} · waived {a.waived} · discoveries {a.discoveries}
          </p>
          <p className="mt-1 text-gray-600 dark:text-gray-300">
            completeness {run.completeness ?? 'unknown'} · failures{' '}
            {run.failureClasses.join(', ') || 'none'} · cost{' '}
            {run.cost.total === null ? 'unknown' : run.cost.total}
            {run.cost.capHit ? ' (cap hit)' : ''}
          </p>
          {run.stability ? (
            <p className="mt-1 text-gray-600 dark:text-gray-300">
              stability: {run.stability.repeats} repeats, disagreed{' '}
              {run.stability.disagreed.join(', ') || 'none'}
            </p>
          ) : null}
        </div>
        <div>
          <p className="font-semibold text-gray-700 dark:text-gray-200">Versions</p>
          <p className="mt-1 break-all font-mono text-[11px] text-gray-600 dark:text-gray-300">
            corpus {run.versions.corpus ?? '—'} · model {run.versions.model ?? '—'} · prompt{' '}
            {run.versions.prompt ?? '—'} · engine {run.versions.engine ?? '—'} · assertion{' '}
            {run.versions.assertion ?? '—'} · sha {run.sha ?? '—'}
          </p>
          {ravenLink ? (
            <a
              href={ravenLink}
              className="mt-1 inline-block font-medium text-blue-600 hover:underline dark:text-blue-400"
            >
              RAVEN release {run.releaseId}
            </a>
          ) : null}
        </div>
        <div>
          <p className="font-semibold text-gray-700 dark:text-gray-200">Calibrated score</p>
          {run.score ? (
            <p className="mt-1 text-gray-600 dark:text-gray-300">
              TP {run.score.tp} · FP {run.score.fp} · FN {run.score.fn} · TN {run.score.tn} ·
              unknown {run.score.unknown} · recall {pct(run.score.recall.point)} (lower{' '}
              {pct(run.score.recall.lower)}, n={run.score.recall.n}) · precision{' '}
              {pct(run.score.precision.point)} (n={run.score.precision.n}) · completeness{' '}
              {pct(run.score.completeness)} · within-run agreement{' '}
              {pct(run.score.withinRunAgreementRate)} · baseline{' '}
              {run.score.baselineState ?? 'unknown'} · tiers: release-blocking{' '}
              {run.score.policy.release_blocking ?? '—'}, auto-filing{' '}
              {run.score.policy.auto_filing ?? '—'}, advisory {run.score.policy.advisory ?? '—'}
            </p>
          ) : (
            <p className="mt-1 text-amber-700 dark:text-amber-300">
              This run was not scored; no calibration verdict exists for it.
            </p>
          )}
          {run.action ? (
            <p className="mt-2 text-gray-600 dark:text-gray-300">
              Action policy: requested {run.action.modeRequested ?? '—'}, effective{' '}
              {run.action.modeEffective ?? '—'} · block {run.action.blockDecisions} · file{' '}
              {run.action.fileDecisions} · queued for adjudication{' '}
              {run.action.queuedForAdjudication} · notifications {run.action.notifications} ·
              missing evidence {run.action.missingEvidence}
              {run.action.missingEvidenceClasses.length
                ? ` (${run.action.missingEvidenceClasses.join(', ')})`
                : ''}{' '}
              · validation {run.action.validation} · attestation {run.action.outcome ?? 'none'}
            </p>
          ) : null}
        </div>
      </div>
    </details>
  );
}

function FindingRow({ finding }: { finding: PrismWorkbenchFinding }) {
  return (
    <li
      data-testid="prism-finding-row"
      data-check-type={finding.checkType}
      data-lifecycle={finding.lifecycle}
      className="border-b border-gray-200 px-4 py-3 last:border-b-0 dark:border-zinc-700"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={finding.lifecycle === 'active' ? 'red' : 'green'}>{finding.lifecycle}</Badge>
        <Badge tone={finding.checkType === 'judged' ? 'blue' : 'neutral'}>
          {display(finding.checkType)}
        </Badge>
        <Badge>{finding.severity}</Badge>
        <span className="font-mono text-xs text-gray-900 dark:text-white">
          {finding.assertionId}
        </span>
        {finding.semanticKey ? (
          <span className="break-all font-mono text-[11px] text-gray-500">
            {finding.semanticKey}
          </span>
        ) : null}
        <a
          href={`/admin/platform-health/critical?finding=${encodeURIComponent(finding.findingId)}`}
          className="text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
        >
          AXON finding
        </a>
      </div>
      <p className="mt-1 text-sm text-gray-700 dark:text-gray-200">{finding.summary}</p>
      <p className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-gray-500 dark:text-gray-400">
        <span>
          {finding.scope.appId ?? 'unknown app'} · {finding.scope.store ?? '?'} ·{' '}
          {finding.scope.locale ?? '?'} · {finding.scope.engine ?? '?'}
        </span>
        <span>
          {display(finding.eventType)} #{finding.sequence} · {timestamp(finding.occurredAt)}
        </span>
        {finding.adjudicationId ? <span>adjudication {finding.adjudicationId}</span> : null}
        {finding.waiverId ? <span>waiver {finding.waiverId}</span> : null}
        {finding.revalidationAttestationId ? (
          <span>recovered by fresh PRISM attestation</span>
        ) : null}
        {finding.receipts.map((receipt) => (
          <span key={receipt.actionReferenceId}>
            MANTIS {receipt.system} {receipt.externalId} ({receipt.status})
          </span>
        ))}
      </p>
    </li>
  );
}

export function PrismWorkbenchContent({ payload }: { payload: PlatformHealthPrismResponse }) {
  const { workbench } = payload;
  const parity = workbench.assertions.parity;
  return (
    <div className="space-y-4">
      {!payload.available ? (
        <div
          data-testid="prism-unavailable"
          className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100"
        >
          <strong>PRISM evidence is unavailable.</strong> Registered checks remain visible, but
          absence of evidence is not an all-clear.
        </div>
      ) : null}
      {payload.partial ? (
        <div
          data-testid="prism-partial"
          className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
        >
          <strong>Partial or inconsistent evidence.</strong>
          <ul className="mt-2 list-disc pl-5 text-xs">
            {[...payload.gaps.map((gap) => gap.message), ...workbench.gaps].map((message, i) => (
              <li key={`${i}:${message}`}>{message}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <VerdictBanner workbench={workbench} />

      <dl className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
        <Metric label="Registered checks" value={workbench.registry.checks.length} />
        <Metric label="Never seen" value={workbench.registry.counts.never_seen} warning />
        <Metric label="Missing evaluations" value={workbench.selfHealth.missing} warning />
        <Metric label="Errored evaluations" value={workbench.selfHealth.errored} warning />
        <Metric label="Open findings" value={workbench.findingCounts.active} warning />
        <Metric label="Recovered" value={workbench.findingCounts.recovered} />
        <Metric label="Adjudication queue" value={workbench.actions.adjudicationQueue} warning />
        <Metric label="MANTIS receipts" value={workbench.actions.receipts.total} />
      </dl>

      <section
        data-testid="prism-axon-parity"
        className={cn(
          'rounded-lg border p-4 text-sm',
          payload.axonComparison.matches
            ? 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100'
            : 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100'
        )}
      >
        <div className="flex items-center gap-2 font-semibold">
          <ShieldCheck className="h-4 w-4" aria-hidden="true" /> AXON rollup:{' '}
          {payload.axonComparison.matches ? 'matches' : 'unverified or mismatched'}
        </div>
        <p className="mt-1 text-xs">
          AXON active PRISM findings {payload.axonComparison.axonActiveFindings ?? 'unknown'} ·
          workbench ledger {payload.axonComparison.workbenchActiveFindings} · AXON state{' '}
          {payload.axonComparison.axonState ?? 'not reduced'}. The rollup is compared, never
          substituted: PRISM&apos;s verdict above is its own.
        </p>
      </section>

      <Section
        title="Quality registry and exact denominators"
        subtitle={`Registered PRISM checks in ${workbench.environment}, evidence over the last ${workbench.window.days} days`}
        testId="prism-registry"
      >
        <ul>
          {workbench.registry.checks.map((check) => (
            <CheckRow key={check.checkId} check={check} />
          ))}
        </ul>
      </Section>

      <Section
        title="Denominators by assertion, release, engine and judge version"
        testId="prism-denominators"
      >
        <div className="grid gap-4 p-4 text-xs lg:grid-cols-2">
          <div>
            <p className="font-semibold text-gray-700 dark:text-gray-200">By assertion</p>
            {workbench.denominators.byAssertion.length === 0 ? (
              <p className="mt-1 text-amber-700 dark:text-amber-300">No evidence in the window.</p>
            ) : (
              <table className="mt-1 w-full text-left">
                <thead className="text-[11px] text-gray-500">
                  <tr>
                    <th className="pr-2">assertion</th>
                    <th className="pr-2">type</th>
                    <th className="pr-2 text-right">audits</th>
                    <th className="pr-2 text-right">passed</th>
                    <th className="pr-2 text-right">failed</th>
                    <th className="pr-2 text-right">missing</th>
                    <th className="pr-2 text-right">errored</th>
                  </tr>
                </thead>
                <tbody>
                  {workbench.denominators.byAssertion.map((row) => (
                    <tr
                      key={`${row.checkId}:${row.assertionId}`}
                      data-testid="prism-assertion-denominator"
                      className="border-t border-gray-100 dark:border-zinc-700"
                    >
                      <td className="pr-2 font-mono">{row.assertionId}</td>
                      <td className="pr-2">{display(row.checkType)}</td>
                      <td className="pr-2 text-right tabular-nums">{row.audits}</td>
                      <td className="pr-2 text-right tabular-nums">{row.counts.passed}</td>
                      <td className="pr-2 text-right tabular-nums">{row.counts.failed}</td>
                      <td className="pr-2 text-right tabular-nums">{row.counts.missing}</td>
                      <td className="pr-2 text-right tabular-nums">{row.counts.errored}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="space-y-3">
            {(
              [
                ['By release', workbench.denominators.byRelease],
                ['By engine', workbench.denominators.byEngine],
              ] as const
            ).map(([label, rows]) => (
              <div key={label}>
                <p className="font-semibold text-gray-700 dark:text-gray-200">{label}</p>
                {rows.length === 0 ? (
                  <p className="mt-1 text-gray-500">none</p>
                ) : (
                  <ul className="mt-1 space-y-0.5 font-mono text-[11px] text-gray-600 dark:text-gray-300">
                    {rows.map((row) => (
                      <li key={row.key ?? '__null'}>
                        {row.key ?? (label === 'By release' ? 'no release' : 'no engine')}:{' '}
                        {row.rows} rows · {row.failed} failed · {row.audits} audits
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
            <div>
              <p className="font-semibold text-gray-700 dark:text-gray-200">
                By judge model / prompt version
              </p>
              {workbench.denominators.byVersion.length === 0 ? (
                <p className="mt-1 text-gray-500">no judged evidence</p>
              ) : (
                <ul className="mt-1 space-y-0.5 font-mono text-[11px] text-gray-600 dark:text-gray-300">
                  {workbench.denominators.byVersion.map((row) => (
                    <li key={`${row.modelVersion}:${row.promptVersion}`}>
                      {row.modelVersion ?? 'unversioned model'} /{' '}
                      {row.promptVersion ?? 'unversioned prompt'}: {row.rows} rows · {row.failed}{' '}
                      failed
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </Section>

      <Section
        title="Audit Assertions — legacy table, PRISM parity and regression history"
        subtitle="The A7Y-2329 surface is measured here, not replaced; cutover follows the parity bake."
        testId="prism-assertions"
      >
        <div
          data-testid="prism-parity"
          data-state={parity.state}
          className={cn(
            'border-b px-4 py-3 text-xs',
            parity.state === 'not_applicable'
              ? 'border-gray-200 bg-gray-50 text-gray-700 dark:border-zinc-700 dark:bg-zinc-900/40 dark:text-gray-200'
              : parity.state === 'parity'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-100'
                : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100'
          )}
        >
          Parity {parity.state}: {parity.matched} matched, {parity.mismatched} mismatched over{' '}
          {parity.rows.length} assertion(s). History on the PRISM ledger: opened{' '}
          {workbench.assertions.history.opened}, evidence added{' '}
          {workbench.assertions.history.evidenceAdded}, reopened{' '}
          {workbench.assertions.history.reopened}, recovered{' '}
          {workbench.assertions.history.recovered}. {parity.window.explanation}{' '}
          <a
            href="/admin/platform-health/audit-assertions"
            className="font-medium text-blue-600 hover:underline dark:text-blue-400"
          >
            Open the legacy Audit Assertions page
          </a>
        </div>
        {parity.rows.length === 0 ? (
          <Empty>No assertion has evidence on either side in the window.</Empty>
        ) : (
          <table className="w-full text-left text-xs">
            <thead className="text-[11px] text-gray-500">
              <tr>
                <th className="px-4 py-2">assertion</th>
                <th className="px-2 py-2 text-right">legacy audits</th>
                <th className="px-2 py-2 text-right">legacy failed</th>
                <th className="px-2 py-2 text-right">PRISM audits</th>
                <th className="px-2 py-2 text-right">PRISM failed</th>
                <th className="px-2 py-2">state</th>
              </tr>
            </thead>
            <tbody>
              {parity.rows.map((row) => (
                <tr
                  key={row.assertionId}
                  data-testid="prism-parity-row"
                  data-state={row.state}
                  className="border-t border-gray-100 dark:border-zinc-700"
                >
                  <td className="px-4 py-1.5 font-mono">{row.assertionId}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{row.legacyAudits ?? '—'}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{row.legacyFailed ?? '—'}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{row.shadowAudits ?? '—'}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{row.shadowFailed ?? '—'}</td>
                  <td className="px-2 py-1.5">
                    <Badge tone={row.state === 'match' ? 'green' : 'amber'}>
                      {display(row.state)}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section
        title="Calibration lanes — corpus coverage, runs, versions, scores and stability"
        subtitle={`Lane evidence over the last ${workbench.window.laneDays} days across every environment; corpus ${workbench.calibration.corpusVersions.join(', ') || 'not observed'}`}
        testId="prism-lanes"
      >
        {workbench.lanes.length === 0 ? (
          <Empty>No lane run has published evidence in the window.</Empty>
        ) : (
          workbench.lanes.map((run) => <LaneRow key={`${run.lane}:${run.runId}`} run={run} />)
        )}
      </Section>

      <Section
        title="Unified PRISM findings"
        subtitle={`${workbench.findingCounts.active} active · ${workbench.findingCounts.recovered} recovered · ${workbench.findingCounts.deterministic} deterministic · ${workbench.findingCounts.judged} judged`}
        testId="prism-findings"
      >
        {workbench.findings.length === 0 ? (
          <Empty>The PRISM finding ledger holds no finding for this environment.</Empty>
        ) : (
          <ul>
            {workbench.findings.map((finding) => (
              <FindingRow key={finding.findingId} finding={finding} />
            ))}
          </ul>
        )}
      </Section>

      <Section
        title="Adjudications, waivers, actions, receipts and recovery"
        subtitle={`Decision store ${workbench.decisions.storeVersion} · action policy ${workbench.decisions.actionPolicy.version} in ${workbench.decisions.actionPolicy.mode} mode`}
        testId="prism-decisions"
      >
        <div className="grid gap-4 p-4 text-xs lg:grid-cols-3">
          <div>
            <p className="font-semibold text-gray-700 dark:text-gray-200">Adjudications</p>
            {workbench.decisions.adjudications.length === 0 ? (
              <p className="mt-1 text-gray-500">none</p>
            ) : (
              <ul className="mt-1 space-y-1">
                {workbench.decisions.adjudications.map((entry) => (
                  <li key={entry.adjudicationId} data-testid="prism-adjudication">
                    <Badge>{display(entry.decision)}</Badge>{' '}
                    <span className="font-mono">{entry.keySummary}</span> — {entry.reason} (
                    {entry.owner}, {entry.sourceTicket}
                    {entry.reviewBy ? `, review by ${timestamp(entry.reviewBy)}` : ''})
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-3 font-semibold text-gray-700 dark:text-gray-200">Waivers</p>
            {workbench.decisions.waivers.length === 0 ? (
              <p className="mt-1 text-gray-500">none</p>
            ) : (
              <ul className="mt-1 space-y-1">
                {workbench.decisions.waivers.map((entry) => (
                  <li key={entry.waiverId} data-testid="prism-waiver" data-state={entry.state}>
                    <Badge tone={entry.state === 'active' ? 'amber' : 'red'}>
                      {display(entry.state)}
                    </Badge>{' '}
                    <span className="font-mono">{entry.keySummary}</span> until{' '}
                    {timestamp(entry.expiresAt)} — {entry.reason} ({entry.owner},{' '}
                    {entry.sourceTicket})
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <p className="font-semibold text-gray-700 dark:text-gray-200">Actions</p>
            <p className="mt-1 text-gray-600 dark:text-gray-300">
              Policy blocks at {workbench.decisions.actionPolicy.blockSeverities.join(', ')}, files
              at {workbench.decisions.actionPolicy.fileSeverities.join(', ')}; mode{' '}
              {workbench.decisions.actionPolicy.mode}. Latest reports:{' '}
              {workbench.actions.latestReports.length} · queued for adjudication{' '}
              {workbench.actions.adjudicationQueue} · would block:{' '}
              {workbench.actions.wouldBlock ? 'yes' : 'no'}.
            </p>
            <p className="mt-3 font-semibold text-gray-700 dark:text-gray-200">MANTIS receipts</p>
            <p className="mt-1 text-gray-600 dark:text-gray-300" data-testid="prism-receipts">
              {workbench.actions.receipts.total === 0
                ? 'No MANTIS receipt references a PRISM finding yet.'
                : `${workbench.actions.receipts.total} receipt(s): ${Object.entries(
                    workbench.actions.receipts.bySystem
                  )
                    .map(([system, n]) => `${system} ${n}`)
                    .join(', ')} · ${Object.entries(workbench.actions.receipts.byStatus)
                    .map(([status, n]) => `${status} ${n}`)
                    .join(', ')}`}
            </p>
          </div>
          <div>
            <p className="font-semibold text-gray-700 dark:text-gray-200">Fresh PRISM recovery</p>
            <p className="mt-1 text-gray-600 dark:text-gray-300" data-testid="prism-recovery">
              {workbench.recovery.recovered.length} finding(s) recovered by a fresh same-scope PRISM
              attestation · {workbench.recovery.reopened.length} reopened ·{' '}
              {workbench.recovery.unproven.length} marked recovered without proof (not counted).
            </p>
            {workbench.recovery.recovered.slice(0, 10).map((finding) => (
              <p key={finding.findingId} className="mt-1 font-mono text-[11px] text-gray-500">
                {finding.assertionId} · {finding.scope.appId ?? '?'} ·{' '}
                {timestamp(finding.occurredAt)}
              </p>
            ))}
          </div>
        </div>
      </Section>

      <Section
        title="Self-health, missing evidence, publication debt, provider and cost errors, compatibility"
        testId="prism-self-health"
      >
        <div className="grid gap-4 p-4 text-xs lg:grid-cols-3">
          <div>
            <p className="font-semibold text-gray-700 dark:text-gray-200">Evidence</p>
            <p className="mt-1 text-gray-600 dark:text-gray-300">
              Never seen: {workbench.selfHealth.neverSeen.join(', ') || 'none'} · missing{' '}
              {workbench.selfHealth.missing} · errored {workbench.selfHealth.errored}.
            </p>
            <p className="mt-1 text-gray-600 dark:text-gray-300" data-testid="prism-debt">
              Publication debt:{' '}
              {workbench.selfHealth.publicationDebt.available
                ? `${workbench.selfHealth.publicationDebt.pending} pending${
                    workbench.selfHealth.publicationDebt.oldestCreatedAt
                      ? `, oldest ${timestamp(workbench.selfHealth.publicationDebt.oldestCreatedAt)}`
                      : ''
                  }`
                : 'unavailable — the debt read did not answer'}
              .
            </p>
          </div>
          <div>
            <p className="font-semibold text-gray-700 dark:text-gray-200">
              Provider, cost and harness errors
            </p>
            <p className="mt-1 text-gray-600 dark:text-gray-300">
              Lane failures:{' '}
              {Object.entries(workbench.selfHealth.failureClasses)
                .map(([cls, n]) => `${cls} ${n}`)
                .join(', ') || 'none'}
              . Missing evidence recorded by the action policy:{' '}
              {Object.entries(workbench.selfHealth.missingEvidenceClasses)
                .map(([cls, n]) => `${cls} ${n}`)
                .join(', ') || 'none'}
              .
            </p>
          </div>
          <div>
            <p className="font-semibold text-gray-700 dark:text-gray-200">Compatibility</p>
            <p className="mt-1 text-gray-600 dark:text-gray-300" data-testid="prism-legacy">
              audit_consistency_findings: {workbench.selfHealth.legacy.auditConsistency.state} (
              {workbench.selfHealth.legacy.auditConsistency.rows} rows, last{' '}
              {timestamp(workbench.selfHealth.legacy.auditConsistency.lastSeen)}) ·
              expert_eye_findings: {workbench.selfHealth.legacy.expertEye.state} (
              {workbench.selfHealth.legacy.expertEye.rows} rows, last{' '}
              {timestamp(workbench.selfHealth.legacy.expertEye.lastRunStartedAt)}).
            </p>
            {workbench.selfHealth.compatibility.length > 0 ? (
              <ul className="mt-1 list-disc pl-4 text-amber-700 dark:text-amber-300">
                {workbench.selfHealth.compatibility.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
      </Section>

      <Section
        title="Review for Yevhen"
        subtitle="What the quality checks found, in ASO terms — no engineering identifiers needed to read it."
        testId="prism-review"
      >
        <p className="px-4 py-3 text-sm text-gray-800 dark:text-gray-100">
          {workbench.review.headline}
        </p>
        {workbench.review.items.length === 0 ? null : (
          <ul>
            {workbench.review.items.map((item) => (
              <li
                key={item.assertionId}
                data-testid="prism-review-item"
                data-label-resolved={item.labelResolved}
                className="border-t border-gray-200 px-4 py-3 dark:border-zinc-700"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-gray-950 dark:text-white">
                    {item.labelResolved ? item.title : 'Unlabelled quality check'}
                  </span>
                  {!item.labelResolved ? (
                    <span
                      data-testid="prism-review-unlabelled"
                      className="rounded bg-amber-100 px-1.5 py-0.5 font-mono text-[11px] text-amber-800 dark:bg-amber-950 dark:text-amber-200"
                    >
                      no plain-language label yet · {item.assertionId}
                    </span>
                  ) : null}
                  <Badge tone={item.checkType === 'judged' ? 'blue' : 'neutral'}>
                    {item.checkType === 'judged' ? 'expert review' : 'fixed rule'}
                  </Badge>
                  <span className="text-xs text-gray-500">{item.audits} audit(s) affected</span>
                </div>
                <p className="mt-1 text-xs text-gray-700 dark:text-gray-200">{item.meaning}</p>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function SelectFilter({
  label,
  value,
  values,
  onChange,
}: {
  label: string;
  value: string;
  values: string[];
  onChange: (value: string) => void;
}) {
  return (
    <label className="text-xs text-gray-600 dark:text-gray-300">
      <span className="sr-only">{label}</span>
      <select
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-9 rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-900 dark:border-zinc-600 dark:bg-zinc-900 dark:text-white"
      >
        {values.map((item) => (
          <option key={item} value={item}>
            {display(item)}
          </option>
        ))}
      </select>
    </label>
  );
}

export function PrismWorkbenchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const environment = searchParams.get('environment') ?? '';
  const query = useQuery({
    queryKey: ['platform-health-prism', environment],
    queryFn: () => api.getPlatformHealthPrism({ environment: environment || undefined }),
    refetchInterval: REFRESH_INTERVAL_MS,
    retry: false,
  });

  function setEnvironment(value: string) {
    const next = new URLSearchParams(searchParams);
    if (!value || value === 'default') next.delete('environment');
    else next.set('environment', value);
    setSearchParams(next);
  }

  return (
    <div className="px-4 py-0">
      <PageHeader
        title="PRISM — ASO quality workbench"
        subtitle="Deterministic audit assertions, calibrated expert review, decisions, actions and recovery"
      />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {query.data?.data.workbench.generatedAt
            ? `Environment ${query.data.data.workbench.environment} · updated ${timestamp(query.data.data.workbench.generatedAt)}`
            : 'Loading PRISM evidence…'}
        </p>
        <div className="flex items-center gap-2">
          <SelectFilter
            label="Environment"
            value={environment || 'default'}
            values={ENVIRONMENTS}
            onChange={setEnvironment}
          />
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:text-gray-200 dark:hover:bg-zinc-700"
          >
            <RefreshCw
              className={cn('h-3.5 w-3.5', query.isFetching && 'animate-spin')}
              aria-hidden="true"
            />
            Refresh
          </button>
        </div>
      </div>
      <PrismPreSendPanel />
      {query.isError ? (
        <div
          data-testid="prism-error"
          className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100"
        >
          <AlertTriangle className="mr-1 inline h-4 w-4" aria-hidden="true" />
          PRISM workbench request failed. No quality conclusion can be drawn.
        </div>
      ) : query.data ? (
        <PrismWorkbenchContent payload={query.data.data} />
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-500 dark:border-zinc-700 dark:bg-zinc-800">
          Loading PRISM registry and evidence…
        </div>
      )}
    </div>
  );
}
